import type { GuidedStep } from '../lib/guidedSteps.ts'
import type { Side, SidePreparation } from '../lib/verificationItems.ts'
import { titleCaseName } from '../lib/titleCase.ts'
import { ComparisonBoard, DiffColumns, SectionLabel } from './ComparisonColumns.tsx'

/**
 * One step of the conferência, with nothing on the receita side.
 *
 * A verification with no receita is not a different kind of thing; it is the
 * same orçamento with nothing to compare it against. So it is drawn the way
 * every other verification is drawn -- the same header, the same steps, the
 * same four sections and boards -- with an em dash where the receita would
 * be. What it never has is a finding, a decision control or a Concluir: there
 * is nothing to answer and nothing to sign off.
 *
 * #208 drew the orçamento on the same boards but on a screen of its own, all
 * steps at once. As the conferência grew a steps rail, per-preparation steps
 * and Atualizar, that screen grew none of them and became a second page to
 * learn again (#300). Now it is one step at a time, inside the real screen.
 */
export function QuotationStep({
  step,
  quotation,
}: {
  step: GuidedStep
  quotation: Side | undefined
}) {
  /*
   * Both names, because the step holds both.
   *
   * This used to branch on `patient` and `prescriber`, which were separate
   * steps. Combining them into `identity` left two comparisons against kinds
   * that no longer exist -- so neither matched, the step fell through to the
   * preparation path, found no preparation, and rendered as its own title with
   * nothing under it.
   */
  if (step.kind === 'identity')
    return (
      <IdentityOnly
        title={step.title}
        values={[
          { label: 'Paciente', value: quotation?.patientName },
          { label: 'Prescritor', value: quotation?.prescriberName },
        ]}
      />
    )

  const preparation = quotation?.preparations.find((one) => one.id === step.item?.preparationId)
  if (preparation === undefined)
    return <span className="text-tH6 text-text-strong">{step.title}</span>
  return <PreparationOnly title={step.title} preparation={preparation} />
}

function IdentityOnly({
  title,
  values,
}: {
  title: string
  values: ReadonlyArray<{ label: string; value: string | undefined }>
}) {
  return (
    <section className="flex flex-col gap-3">
      <span className="text-tH6 text-text-strong">{title}</span>
      {values.map((one) => (
        <div key={one.label} className="flex flex-col gap-1">
          <SectionLabel label={one.label} />
          <ComparisonBoard status="no_issues">
            <DiffColumns
              prescriptionValue="—"
              systemValue={titleCaseName(one.value) || '—'}
              status="no_issues"
            />
          </ComparisonBoard>
        </div>
      ))}
    </section>
  )
}

function PreparationOnly({ title, preparation }: { title: string; preparation: SidePreparation }) {
  const rows = quotationRows(preparation)
  return (
    <section className="flex flex-col gap-3">
      <span className="text-tH6 text-text-strong">{title}</span>

      <div className="flex flex-col gap-2">
        <SectionLabel label="Forma" />
        <ComparisonBoard status="no_issues">
          <DiffColumns prescriptionValue="—" systemValue={rows.form} status="no_issues" />
        </ComparisonBoard>
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel label="Ativos" />
        <ComparisonBoard status="no_issues">
          {rows.ingredients.length === 0 ? (
            <DiffColumns prescriptionValue="—" systemValue="—" status="no_issues" />
          ) : (
            rows.ingredients.map((ingredient, index) => (
              <DiffColumns
                key={index}
                prescriptionValue="—"
                systemValue={ingredient}
                status="no_issues"
              />
            ))
          )}
        </ComparisonBoard>
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel label="Posologia" />
        <ComparisonBoard status="no_issues">
          <DiffColumns prescriptionValue="—" systemValue={rows.posology} status="no_issues" />
        </ComparisonBoard>
      </div>

      <div className="flex flex-col gap-2">
        <SectionLabel label="Volume" />
        <ComparisonBoard status="no_issues">
          <DiffColumns prescriptionValue="—" systemValue={rows.quantity} status="no_issues" />
        </ComparisonBoard>
      </div>
    </section>
  )
}

/**
 * What the four sections say for a keyed preparation, as text.
 *
 * Pure, so the shape is testable without a screen. The form is the ERP's own
 * code on the keyed side -- `'1'`, `'9'` -- and a code is not a name: shown as
 * an em dash rather than as a number nobody can read.
 */
export function quotationRows(preparation: SidePreparation): {
  form: string
  ingredients: string[]
  posology: string
  quantity: string
} {
  const form = preparation.form?.trim() ?? ''
  return {
    form: form === '' || /^\d+$/.test(form) ? '—' : form,
    ingredients: preparation.ingredients.map((one) =>
      one.dose === undefined
        ? one.rawName
        : `${one.rawName} ${formatAmount(one.dose.amount)} ${one.dose.unit}`.trim(),
    ),
    posology: preparation.posology?.trim() || '—',
    quantity:
      preparation.quantity === undefined
        ? '—'
        : `${formatAmount(preparation.quantity.amount)} ${preparation.quantity.unit}`.trim(),
  }
}

function formatAmount(amount: number): string {
  return Number.isInteger(amount) ? String(amount) : String(amount).replace('.', ',')
}
